import { useCallback } from 'react';

import { AnnotationType } from '../../../api/types';
import { ExtendedAnswer } from '../../../components/Chat/Message/Enhanced/ToolCallResults/types';
import { MixpanelEvent } from '../../../services/mixpanel/types';
import { TEXT_HTML, TEXT_PLAIN } from '../../constants';
import { sendMetrics } from '../../thunks/metrics-thunk';
import { ICopyActionMeta } from '../../types';
import { useAnalytics } from '../analytics';
import { useAnswers } from '../answer/answer-hooks';
import { useAppDispatch } from '../app-hooks';
import { useSources } from '../settings-hooks';

import { prepareClipboardItems, showSuccessToast } from './helpers';

export const useCopyAnswer = () => {
    const dispatch = useAppDispatch();
    const [, { data }] = useAnswers();
    const { sources } = useSources();
    const [log] = useAnalytics();

    return useCallback(
        async (meta: ICopyActionMeta) => {
            const { answer, origin } = meta;

            let answerToCopy: ICopyActionMeta['answer'] = answer;

            if (typeof answerToCopy === 'string') {
                answerToCopy = data?.answers.find((answer) => answer.doc_id === answerToCopy);
            }

            if (!answerToCopy) {
                return;
            }

            const { blobs, strings } = prepareClipboardItems(answerToCopy);
            await navigator.clipboard.write([new ClipboardItem(blobs)]);

            const { question, queries, message_id, ..._answer } = answerToCopy as ExtendedAnswer;

            log({ answer: _answer, type: AnnotationType.COPY_ANSWER, question, questionId: message_id });
            dispatch(
                sendMetrics({
                    event: MixpanelEvent.COPY_SNIPPET_MAIN_PANEL,
                    meta: {
                        rich_text: strings[TEXT_HTML],
                        plain_text: strings[TEXT_PLAIN],
                        origin,
                        question,
                        question_id: message_id,
                        sources,
                    },
                    //if no question in answer, populate it from state in `sendMetrics` thunk
                    ...(!question && {
                        injectAnswersMetrics: {
                            answers: data,
                            sources,
                        },
                    }),
                })
            );

            showSuccessToast(origin, !!answerToCopy?.source_is_public);
        },
        [data, log, dispatch, sources]
    );
};
